import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Menu, X, ArrowRight, Globe, Phone, MapPin } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const navItems = [
    { path: '/', label: 'Home', desc: 'Award overview & winners' },
    { path: '/hall-of-fame', label: 'Hall of Fame', desc: 'All winners by year' },
    { path: '/nominations', label: 'Nominations', desc: 'Award categories' },
    { path: '/ceremony', label: 'Ceremony 2026', desc: 'Event details & schedule' },
    { path: '/about', label: 'About Us', desc: 'Our mission & history' },
    { path: '/support', label: 'Support', desc: 'Help & contact' },
  ];

  return (
    <>
      <header className={`site-header ${scrolled ? 'scrolled' : ''}`} data-testid="site-header">
        <div className="header-container">
          <div className="header-main">
            <Link to="/" className="logo-section">
              <img 
                src="https://customer-assets.emergentagent.com/job_global-finance-hub-12/artifacts/7kwrmg9i_World%20Financial%20Award-01.png" 
                alt="World Financial Award" 
                className="logo-image"
              />
            </Link>

            <nav className="desktop-nav">
              <ul className="nav-menu">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <Link 
                      to={item.path}
                      className={location.pathname === item.path ? 'active' : ''}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="header-actions">
              <a href="mailto:info@worldfinancialaward.com" className="contact-link">
                <Mail size={18} />
                <span className="contact-text">info@worldfinancialaward.com</span>
              </a>
              <Link to="/enter" className="header-cta">
                Enter Competition
              </Link>
              <button 
                className={`mobile-menu-toggle ${mobileMenuOpen ? 'is-open' : ''}`}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Full-screen Mobile Menu */}
      <div className={`fullscreen-menu ${mobileMenuOpen ? 'open' : ''}`} data-testid="fullscreen-menu">
        <div className="fm-inner">
          {/* Close button */}
          <button className="fm-close" onClick={() => setMobileMenuOpen(false)} data-testid="fm-close">
            <X size={28} />
          </button>

          {/* Top - Logo */}
          <div className="fm-brand">
            <img 
              src="https://customer-assets.emergentagent.com/job_global-finance-hub-12/artifacts/7kwrmg9i_World%20Financial%20Award-01.png" 
              alt="World Financial Award" 
              className="fm-logo"
            />
          </div>

          {/* Navigation Links */}
          <nav className="fm-nav">
            {navItems.map((item, idx) => (
              <Link
                key={item.path}
                to={item.path}
                className={`fm-nav-item ${location.pathname === item.path ? 'active' : ''}`}
                style={{ animationDelay: `${0.05 + idx * 0.05}s` }}
                data-testid={`fm-nav-${item.path.replace('/', '') || 'home'}`}
              >
                <div className="fm-nav-content">
                  <span className="fm-nav-index">0{idx + 1}</span>
                  <div className="fm-nav-text">
                    <span className="fm-nav-label">{item.label}</span>
                    <span className="fm-nav-desc">{item.desc}</span>
                  </div>
                </div>
                <ArrowRight size={18} className="fm-nav-arrow" />
              </Link>
            ))}
          </nav>

          {/* CTA */}
          <Link to="/enter" className="fm-cta" onClick={() => setMobileMenuOpen(false)} data-testid="fm-cta">
            Enter Competition
            <ArrowRight size={18} />
          </Link>

          {/* Footer Info */}
          <div className="fm-footer">
            <div className="fm-footer-row">
              <a href="mailto:info@worldfinancialaward.com" className="fm-footer-link">
                <Mail size={15} /> info@worldfinancialaward.com
              </a>
            </div>
            <div className="fm-footer-row">
              <a href="tel:+442012345678" className="fm-footer-link">
                <Phone size={15} /> +44 (0) 20 1234 5678
              </a>
            </div>
            <div className="fm-footer-row">
              <span className="fm-footer-link">
                <MapPin size={15} /> London, United Kingdom
              </span>
            </div>
            <div className="fm-socials">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="fm-social" aria-label="Facebook">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="fm-social" aria-label="X">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="fm-social" aria-label="LinkedIn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            </div>
          </div>
        </div>

        {/* Decorative accent line */}
        <div className="fm-accent-line"></div>
      </div>
    </>
  );
};

export default Header;
